import { type ReactElement, type ReactNode, useState } from "react";
import { Alert, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import FunctionsOutlinedIcon from "@mui/icons-material/FunctionsOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../../assets/theme/custom/hooks";

type DocsTopic = "overview" | "table" | "formulas" | "echarts" | "text" | "html" | "backend";

const TOPICS: DocsTopic[] = ["overview", "table", "formulas", "echarts", "text", "html", "backend"];

const TOPIC_ICONS: Record<DocsTopic, ReactElement> = {
  overview: <InfoOutlinedIcon fontSize="small" />,
  table: <TableChartOutlinedIcon fontSize="small" />,
  formulas: <FunctionsOutlinedIcon fontSize="small" />,
  echarts: <BarChartOutlinedIcon fontSize="small" />,
  text: <NotesOutlinedIcon fontSize="small" />,
  html: <CodeOutlinedIcon fontSize="small" />,
  backend: <DnsOutlinedIcon fontSize="small" />,
};

const EXAMPLE_METRIC_DSL = `{
  "size": 0,
  "query": {
    "bool": {
      "filter": [
        {
          "range": {
            "{{timeField}}": {
              "gte": "{{from}}",
              "lte": "{{to}}",
              "format": "strict_date_optional_time"
            }
          }
        }
      ]
    }
  },
  "aggs": {
    "adet": { "value_count": { "field": "_id" } },
    "alis_tutari": { "sum": { "field": "amount" } }
  }
}`;

const EXAMPLE_TABLE_KPI_MAPPING = `{
  "columns": [
    { "header": "GELEN ADET", "field": "adet", "path": "adet" },
    { "header": "ALIŞ TUTARI", "field": "alis_tutari", "path": "alis_tutari" },
    {
      "header": "ORT. ALIŞ TUTARI",
      "field": "ort_alis_tutari",
      "formula": "{{alis_tutari}} / {{adet}}"
    },
    {
      "header": "ORT.GÜN ADET",
      "field": "ort_gun_adet",
      "formula": "{{adet}} / {{range.days}}"
    },
    {
      "header": "ORT.GÜN TUTAR",
      "field": "ort_gun_tutar",
      "formula": "{{alis_tutari}} / {{range.days}}"
    },
    {
      "header": "ÖNG.CİRO",
      "field": "ong_ciro",
      "formula": "{{alis_tutari}} / {{range.elapsedDays}} * {{calendar.daysInMonth}}"
    }
  ]
}`;

const EXAMPLE_TABLE_TERMS_MAPPING = `{
  "columns": [
    { "header": "Product", "field": "product", "path": "key" },
    { "header": "Count", "field": "count", "path": "doc_count" },
    { "header": "Amount", "field": "amount", "path": "alis_tutari" }
  ]
}`;

const EXAMPLE_TABLE_HITS_MAPPING = `{
  "rowSource": "hits.productOrderItem",
  "columns": [
    { "header": "Product", "field": "productName", "path": "product.name" },
    { "header": "Quantity", "field": "quantity", "path": "quantity" },
    { "header": "Line total", "field": "line_total", "formula": "{{quantity}} * {{price}}" }
  ]
}`;

const EXAMPLE_ECHARTS_MAPPING = `{
  "chartType": "bar",
  "orientation": "vertical",
  "optionTemplate": {
    "title": { "text": "{{widget.name}}" },
    "tooltip": { "trigger": "axis" },
    "xAxis": { "type": "category", "data": [] },
    "yAxis": { "type": "value" },
    "series": [{ "type": "bar", "data": [] }]
  }
}`;

const EXAMPLE_ECHARTS_PIE = `{
  "chartType": "pie",
  "optionTemplate": {
    "title": { "text": "Channel share" },
    "tooltip": { "trigger": "item" },
    "series": [{ "type": "pie", "radius": "55%", "data": [] }]
  }
}`;

const EXAMPLE_TEXT_MAPPING = `{
  "template": "{{widget.title}}: {{hits.total.value}} records in the selected range."
}`;

const EXAMPLE_HTML_MAPPING = `{
  "template": "<h3>{{widget.title}}</h3><p>Total: <strong>{{hits.total.value}}</strong></p>"
}`;

const EXAMPLE_PARAMETERS = `[
  { "name": "from", "type": "datetime", "bind": "dashboard.dateFrom", "required": true },
  { "name": "to", "type": "datetime", "bind": "dashboard.dateTo", "required": true }
]`;

const EXAMPLE_FORMULA_DAILY = "{{adet}} / {{range.days}}";
const EXAMPLE_FORMULA_WEEKDAY = "safeDiv({{adet}}, {{range.weekdays}})";
const EXAMPLE_FORMULA_FORECAST =
  "{{alis_tutari}} / {{range.elapsedDays}} * {{calendar.daysInMonth}}";
const EXAMPLE_FORMULA_RUNRATE =
  "if({{range.isComplete}}, {{adet}}, {{adet}} / {{range.elapsedDays}} * {{range.days}})";
const EXAMPLE_FORMULA_SHARE = "round(pct({{adet}}, {{sum.adet}}), 1)";
const EXAMPLE_FORMULA_DELTA = "{{delta.adet}}\n{{deltaPct.adet}}";
const EXAMPLE_FORMULA_SAFE =
  "if({{adet}} > 0, round(safeDiv({{alis_tutari}}, {{adet}}), 2), 0)";

function useDocItems(key: string): string[] {
  const { t } = useTranslation();
  const value = t(key, { returnObjects: true });
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
}

function DocsCode({ children }: { children: string }) {
  const custom = useAppCustomStyles();
  return (
    <Box component="pre" sx={custom.components.widgets.docsCode}>
      {children}
    </Box>
  );
}

function DocsCaption({ children }: { children: ReactNode }) {
  const custom = useAppCustomStyles();
  return (
    <Typography variant="caption" color="text.secondary" sx={custom.components.widgets.docsCaption}>
      {children}
    </Typography>
  );
}

function DocsHeading({ children }: { children: ReactNode }) {
  const custom = useAppCustomStyles();
  return (
    <Typography variant="subtitle1" fontWeight={700} sx={custom.components.widgets.docsSectionTitle}>
      {children}
    </Typography>
  );
}

function DocsList({ items }: { items: string[] }) {
  const custom = useAppCustomStyles();
  return (
    <Box component="ul" sx={custom.components.widgets.docsList}>
      {items.map((item, index) => (
        <Typography key={`${index}-${item}`} component="li" variant="body2" sx={custom.components.widgets.docsListItem}>
          {item}
        </Typography>
      ))}
    </Box>
  );
}

function InlineCode({ children }: { children: string }) {
  const custom = useAppCustomStyles();
  return (
    <Box component="code" sx={custom.components.widgets.docsInlineCode}>
      {children}
    </Box>
  );
}

function OverviewDocs() {
  const { t } = useTranslation();
  const formatItems = useDocItems("widgetDocs.overview.formatItems");
  const studioItems = useDocItems("widgetDocs.overview.studioItems");
  const actionItems = useDocItems("widgetDocs.overview.actionItems");
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{t("widgetDocs.overview.intro")}</Typography>
      <DocsHeading>{t("widgetDocs.overview.formatsTitle")}</DocsHeading>
      <DocsList items={formatItems} />
      <DocsHeading>{t("widgetDocs.overview.studioTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.overview.studioIntro")}</Typography>
      <DocsList items={studioItems} />
      <DocsHeading>{t("widgetDocs.overview.actionsTitle")}</DocsHeading>
      <DocsList items={actionItems} />
    </Stack>
  );
}

function TableDocs() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const uiItems = useDocItems("widgetDocs.table.uiItems");
  const columnModeItems = useDocItems("widgetDocs.table.columnModeItems");
  const formulaItems = useDocItems("widgetDocs.table.formulaItems");
  const backendItems = useDocItems("widgetDocs.table.backendItems");
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{t("widgetDocs.table.intro")}</Typography>
      <DocsHeading>{t("widgetDocs.table.uiTitle")}</DocsHeading>
      <DocsList items={uiItems} />
      <DocsHeading>{t("widgetDocs.table.columnModesTitle")}</DocsHeading>
      <DocsList items={columnModeItems} />
      <DocsHeading>{t("widgetDocs.table.formulaTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.table.formulaIntro")}</Typography>
      <DocsList items={formulaItems} />
      <Typography variant="body2">{t("widgetDocs.table.formulaGuideHint")}</Typography>
      <Alert severity="warning" sx={custom.components.widgets.docsAlert}>
        {t("widgetDocs.table.formulaIndexWarning")}
      </Alert>
      <DocsHeading>{t("widgetDocs.table.backendTitle")}</DocsHeading>
      <DocsList items={backendItems} />
      <DocsHeading>{t("widgetDocs.table.examplesTitle")}</DocsHeading>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.table.exampleDslTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_METRIC_DSL}</DocsCode>
      <DocsCaption>{t("widgetDocs.table.exampleDslCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.table.exampleKpiTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_TABLE_KPI_MAPPING}</DocsCode>
      <DocsCaption>{t("widgetDocs.table.exampleKpiCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.table.exampleTermsTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_TABLE_TERMS_MAPPING}</DocsCode>
      <DocsCaption>{t("widgetDocs.table.exampleTermsCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.table.exampleHitsTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_TABLE_HITS_MAPPING}</DocsCode>
      <DocsCaption>{t("widgetDocs.table.exampleHitsCaption")}</DocsCaption>
    </Stack>
  );
}

function FormulaDocs() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const quickStartItems = useDocItems("widgetDocs.formulas.quickStartItems");
  const howItems = useDocItems("widgetDocs.formulas.howItems");
  const timezoneItems = useDocItems("widgetDocs.formulas.timezoneItems");
  const refsItems = useDocItems("widgetDocs.formulas.refsItems");
  const rangeItems = useDocItems("widgetDocs.formulas.rangeItems");
  const runRateItems = useDocItems("widgetDocs.formulas.runRateItems");
  const calendarItems = useDocItems("widgetDocs.formulas.calendarItems");
  const constItems = useDocItems("widgetDocs.formulas.constItems");
  const windowItems = useDocItems("widgetDocs.formulas.windowItems");
  const functionsItems = useDocItems("widgetDocs.formulas.functionsItems");
  const operatorsItems = useDocItems("widgetDocs.formulas.operatorsItems");
  const aliasesItems = useDocItems("widgetDocs.formulas.aliasesItems");
  const pitfallsItems = useDocItems("widgetDocs.formulas.pitfallsItems");
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{t("widgetDocs.formulas.intro")}</Typography>
      <DocsHeading>{t("widgetDocs.formulas.quickStartTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.quickStartIntro")}</Typography>
      <DocsList items={quickStartItems} />
      <DocsHeading>{t("widgetDocs.formulas.howTitle")}</DocsHeading>
      <DocsList items={howItems} />
      <DocsHeading>{t("widgetDocs.formulas.timezoneTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.timezoneIntro")}</Typography>
      <DocsList items={timezoneItems} />
      <DocsHeading>{t("widgetDocs.formulas.refsTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.refsIntro")}</Typography>
      <DocsList items={refsItems} />
      <DocsHeading>{t("widgetDocs.formulas.rangeTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.rangeIntro")}</Typography>
      <DocsList items={rangeItems} />
      <DocsHeading>{t("widgetDocs.formulas.runRateTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.runRateIntro")}</Typography>
      <DocsList items={runRateItems} />
      <DocsHeading>{t("widgetDocs.formulas.calendarTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.calendarIntro")}</Typography>
      <DocsList items={calendarItems} />
      <DocsHeading>{t("widgetDocs.formulas.constTitle")}</DocsHeading>
      <DocsList items={constItems} />
      <DocsHeading>{t("widgetDocs.formulas.windowTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.windowIntro")}</Typography>
      <DocsList items={windowItems} />
      <DocsHeading>{t("widgetDocs.formulas.functionsTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.functionsIntro")}</Typography>
      <DocsList items={functionsItems} />
      <DocsHeading>{t("widgetDocs.formulas.operatorsTitle")}</DocsHeading>
      <DocsList items={operatorsItems} />
      <DocsHeading>{t("widgetDocs.formulas.aliasesTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.aliasesIntro")}</Typography>
      <DocsList items={aliasesItems} />
      <DocsHeading>{t("widgetDocs.formulas.recipesTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.formulas.recipesIntro")}</Typography>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.formulas.recipeDailyTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_FORMULA_DAILY}</DocsCode>
      <DocsCaption>{t("widgetDocs.formulas.recipeDailyCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.formulas.recipeWeekdayTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_FORMULA_WEEKDAY}</DocsCode>
      <DocsCaption>{t("widgetDocs.formulas.recipeWeekdayCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.formulas.recipeForecastTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_FORMULA_FORECAST}</DocsCode>
      <DocsCaption>{t("widgetDocs.formulas.recipeForecastCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.formulas.recipeRunRateTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_FORMULA_RUNRATE}</DocsCode>
      <DocsCaption>{t("widgetDocs.formulas.recipeRunRateCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.formulas.recipeShareTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_FORMULA_SHARE}</DocsCode>
      <DocsCaption>{t("widgetDocs.formulas.recipeShareCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.formulas.recipeDeltaTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_FORMULA_DELTA}</DocsCode>
      <DocsCaption>{t("widgetDocs.formulas.recipeDeltaCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.formulas.recipeSafeTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_FORMULA_SAFE}</DocsCode>
      <DocsCaption>{t("widgetDocs.formulas.recipeSafeCaption")}</DocsCaption>
      <DocsHeading>{t("widgetDocs.formulas.pitfallsTitle")}</DocsHeading>
      <DocsList items={pitfallsItems} />
      <Alert severity="info" sx={custom.components.widgets.docsAlert}>
        {t("widgetDocs.formulas.chipsHint")}
      </Alert>
    </Stack>
  );
}

function EchartsDocs() {
  const { t } = useTranslation();
  const uiItems = useDocItems("widgetDocs.echarts.uiItems");
  const typeItems = useDocItems("widgetDocs.echarts.typeItems");
  const backendItems = useDocItems("widgetDocs.echarts.backendItems");
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{t("widgetDocs.echarts.intro")}</Typography>
      <DocsHeading>{t("widgetDocs.echarts.uiTitle")}</DocsHeading>
      <DocsList items={uiItems} />
      <DocsHeading>{t("widgetDocs.echarts.typesTitle")}</DocsHeading>
      <Typography variant="body2">{t("widgetDocs.echarts.typesIntro")}</Typography>
      <DocsList items={typeItems} />
      <DocsHeading>{t("widgetDocs.echarts.backendTitle")}</DocsHeading>
      <DocsList items={backendItems} />
      <DocsHeading>{t("widgetDocs.echarts.examplesTitle")}</DocsHeading>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.echarts.exampleBarTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_ECHARTS_MAPPING}</DocsCode>
      <DocsCaption>{t("widgetDocs.echarts.exampleBarCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.echarts.examplePieTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_ECHARTS_PIE}</DocsCode>
      <DocsCaption>{t("widgetDocs.echarts.examplePieCaption")}</DocsCaption>
    </Stack>
  );
}

function TextDocs() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const uiItems = useDocItems("widgetDocs.text.uiItems");
  const backendItems = useDocItems("widgetDocs.text.backendItems");
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{t("widgetDocs.text.intro")}</Typography>
      <DocsHeading>{t("widgetDocs.text.uiTitle")}</DocsHeading>
      <DocsList items={uiItems} />
      <DocsHeading>{t("widgetDocs.text.placeholdersTitle")}</DocsHeading>
      <Typography variant="body2" sx={custom.components.widgets.docsPlaceholderIntro}>
        {t("widgetDocs.text.placeholdersIntro")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={custom.components.widgets.docsPlaceholderRow}>
        <InlineCode>{"{{widget.title}}"}</InlineCode>
        <InlineCode>{"{{hits.total.value}}"}</InlineCode>
      </Stack>
      <DocsHeading>{t("widgetDocs.text.backendTitle")}</DocsHeading>
      <DocsList items={backendItems} />
      <DocsHeading>{t("widgetDocs.text.exampleTitle")}</DocsHeading>
      <DocsCode>{EXAMPLE_TEXT_MAPPING}</DocsCode>
      <DocsCaption>{t("widgetDocs.text.exampleCaption")}</DocsCaption>
    </Stack>
  );
}

function HtmlDocs() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const uiItems = useDocItems("widgetDocs.html.uiItems");
  const backendItems = useDocItems("widgetDocs.html.backendItems");
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{t("widgetDocs.html.intro")}</Typography>
      <DocsHeading>{t("widgetDocs.html.uiTitle")}</DocsHeading>
      <DocsList items={uiItems} />
      <DocsHeading>{t("widgetDocs.html.placeholdersTitle")}</DocsHeading>
      <Typography variant="body2" sx={custom.components.widgets.docsPlaceholderIntro}>
        {t("widgetDocs.html.placeholdersIntro")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={custom.components.widgets.docsPlaceholderRow}>
        <InlineCode>{"{{widget.title}}"}</InlineCode>
        <InlineCode>{"{{hits.total.value}}"}</InlineCode>
      </Stack>
      <DocsHeading>{t("widgetDocs.html.backendTitle")}</DocsHeading>
      <DocsList items={backendItems} />
      <DocsHeading>{t("widgetDocs.html.exampleTitle")}</DocsHeading>
      <DocsCode>{EXAMPLE_HTML_MAPPING}</DocsCode>
      <DocsCaption>{t("widgetDocs.html.exampleCaption")}</DocsCaption>
    </Stack>
  );
}

function BackendDocs() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const pipelineItems = useDocItems("widgetDocs.backend.pipelineItems");
  const ruleItems = useDocItems("widgetDocs.backend.ruleItems");
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{t("widgetDocs.backend.intro")}</Typography>
      <DocsHeading>{t("widgetDocs.backend.pipelineTitle")}</DocsHeading>
      <DocsList items={pipelineItems} />
      <DocsHeading>{t("widgetDocs.backend.placeholdersTitle")}</DocsHeading>
      <Typography variant="body2" sx={custom.components.widgets.docsPlaceholderIntro}>
        {t("widgetDocs.backend.placeholdersIntro")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={custom.components.widgets.docsPlaceholderRow}>
        <InlineCode>{"{{from}}"}</InlineCode>
        <InlineCode>{"{{to}}"}</InlineCode>
        <InlineCode>{"{{timeField}}"}</InlineCode>
        <InlineCode>{"{{widget.title}}"}</InlineCode>
      </Stack>
      <DocsCaption>{t("widgetDocs.backend.placeholdersCaption")}</DocsCaption>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("widgetDocs.backend.parametersTitle")}
      </Typography>
      <DocsCode>{EXAMPLE_PARAMETERS}</DocsCode>
      <DocsCaption>{t("widgetDocs.backend.parametersCaption")}</DocsCaption>
      <DocsHeading>{t("widgetDocs.backend.rulesTitle")}</DocsHeading>
      <DocsList items={ruleItems} />
      <Alert severity="info" sx={custom.components.widgets.docsAlert}>
        {t("widgetDocs.backend.pipelineAggWarning")}
      </Alert>
    </Stack>
  );
}

export default function WidgetDocsPanel() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const [topic, setTopic] = useState<DocsTopic>("overview");

  return (
    <Box>
      <Tabs
        value={topic}
        onChange={(_, value: DocsTopic) => setTopic(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={custom.components.widgets.docsTabs}
      >
        {TOPICS.map((id) => (
          <Tab
            key={id}
            value={id}
            icon={TOPIC_ICONS[id]}
            iconPosition="start"
            label={t(`widgetDocs.topics.${id}`)}
          />
        ))}
      </Tabs>
      {topic === "overview" && <OverviewDocs />}
      {topic === "table" && <TableDocs />}
      {topic === "formulas" && <FormulaDocs />}
      {topic === "echarts" && <EchartsDocs />}
      {topic === "text" && <TextDocs />}
      {topic === "html" && <HtmlDocs />}
      {topic === "backend" && <BackendDocs />}
    </Box>
  );
}
